import React, { useState } from "react";
import { formatAmount, formatDateTime } from "../utils/format";
import { useTranslation } from "react-i18next";
import deleteIcon from "../icons/trashR.svg";
import ChevronDown from "../icons/chevron-down.svg";
import ChevronUp from "../icons/chevron-upG.svg";
import noImage from "../icons/noImageIcon.svg";

const Rows = ({ columns = [], data = [], onRowClick, expandable = false, onDelete, wrapLayout = false }) => {
  const [expandedRows, setExpandedRows] = useState({});
  const { t } = useTranslation();

  const toggleRow = (rowIndex) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowIndex]: !prev[rowIndex],
    }));
  };

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "25px";
    e.target.style.height = "25px";
  };

  // Calculate column widths for consistent alignment
  const getColumnWidth = (column) => {
    if (column.key === "actions" || column.key === "actionss") {
      return "w-[80px] flex-none";
    }
    return "flex-1 min-w-0"; // min-w-0 allows flex items to shrink below content size
  };

  return (
    <div
      className={`w-full ${wrapLayout ? "overflow-y-auto overflow-x-hidden" : "overflow-x-auto"}`}
    >
      {/* Header */}
      <div
        className={`w-full flex items-center pt-4 pb-2 px-2 text-[#919191] text-[12px] font-semibold 
    ${wrapLayout ? "flex-wrap" : ""}`}
      >
        {expandable && (
          <div className="flex-none w-[40px]"></div>
        )}

        {columns.map((column, index) => (
          <div
            key={index}
            className={`px-2 text-left ${getColumnWidth(column)}
        ${wrapLayout ? "break-words" : "overflow-hidden"} 
        ${column.key === "actions" || column.key === "actionss" ? "text-[#043F88] font-bold text-[16px] whitespace-nowrap" : ""}`}
          >
            {column.label}
          </div>
        ))}
      </div>

      {/* Data Rows */}
      <div className="pb-4">
        {data.map((row, rowIndex) => (
          <div key={rowIndex} className="mb-2">
            {/* Main Row */}
            <div
              className={`flex items-center bg-white py-4 px-2 rounded-[12px] shadow-[0px_6px_26px_#626B7D12] cursor-pointer min-h-[72px] hover:bg-blue-50 transition-colors duration-200 
                ${expandedRows[rowIndex] ? "border-[1px] border-primary" : ""} 
                ${wrapLayout ? "flex-wrap" : ""}`}
              onClick={() => onRowClick && onRowClick(row)}
            >
              {expandable && (
                <button
                  className="flex-none w-[40px] flex items-center justify-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleRow(rowIndex);
                  }}
                  aria-label={expandedRows[rowIndex] ? t("rows.collapse") : t("rows.expand")}
                >
                  <img
                    src={expandedRows[rowIndex] ? ChevronUp : ChevronDown}
                    alt=""
                    className="w-5 h-5"
                  />
                </button>
              )}

              {columns.map((col, colIndex) => {
                const rawValue = row[col.key];
                
                const shouldSplitDateTime = col.splitDateTime;
                let dateTimeString = null;
                if (shouldSplitDateTime && rawValue) {
                  dateTimeString = formatDateTime(rawValue);
                  if (!dateTimeString || dateTimeString === "") {
                    dateTimeString = null;
                  }
                }
                
                const renderedContent = shouldSplitDateTime && dateTimeString ? null : (
                  col.render
                    ? col.render(rawValue, row)
                    : col.key === "amount" || col.key === "attachedAmount"
                      ? formatAmount(rawValue)
                      : rawValue || "N/A"
                );
                
                return (
                  <div
                    key={colIndex}
                    className={`px-2 text-left text-[#38485C] text-[16px] font-semibold ${getColumnWidth(col)}
      ${wrapLayout ? "break-words" : dateTimeString ? "" : "overflow-hidden text-ellipsis whitespace-nowrap"}
      ${col.key === "actions" || col.key === "actionss" ? "flex items-center justify-end" : ""}
      ${dateTimeString ? "flex flex-col" : ""}`}
                    title={wrapLayout || dateTimeString ? undefined : (typeof rawValue === 'string' ? rawValue : undefined)}
                  >
                    {dateTimeString ? (
                      <>
                        <span className="text-[16px] font-semibold text-[#38485C]">{dateTimeString.split(' ')[0]}</span>
                        <span className="text-[16px] font-semibold text-[#38485C]">{dateTimeString.split(' ').slice(1).join(' ')}</span>
                      </>
                    ) : (
                      renderedContent
                    )}
                  </div>
                );
              })}
            </div>

            {/* Expanded Row */}
            {expandable &&
              expandedRows[rowIndex] &&
              (row.productQuantities?.length > 0 || row.products?.length > 0) && (
                <div className="mt-2 pt-[8px] pl-8">
                  <div className="flex flex-col gap-4">
                    {(row.productQuantities || row.products).map((product, productIndex) => {
                      const image = product.image || product.product?.image || noImage;
                      const reference = product.productReference || product.product?.reference || "N/A";
                      const name = product.productName || product.product?.name || "Unknown";
                      const quantity = product.quantity || "N/A";

                      return (
                        <div
                          key={productIndex}
                          className="flex items-center bg-white py-2 px-4 rounded-[12px] shadow-[0px_4px_16px_rgba(98,107,125,0.05)]"
                        >
                          {/* Product Image with Fallback */}
                          <div className="flex-none w-16 h-16 bg-white shadow-[0px_4px_16px_#626B7D12] rounded-md overflow-hidden mr-4 flex items-center justify-center">
                            <img
                              src={image}
                              alt=""
                              className="object-contain w-full h-full"
                              onError={handleImageError}
                              loading="lazy"
                            />
                          </div>

                          {/* Product Details */}
                          <div className="flex flex-wrap w-full gap-x-4 gap-y-2">
                            <div className="flex-1 max-w-[250px]">
                              <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.reference`)}</p>
                              <p className="text-[#38485C] text-[16px] font-semibold break-words">{reference}</p>
                            </div>

                            <div className="flex-1 max-w-[450px]">
                              <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.name`)}</p>
                              <p className="text-[#38485C] text-[16px] font-semibold break-words">{name}</p>
                            </div>

                            <div className="flex-1 max-w-[250px]">
                              <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.quantity`)}</p>
                              <p className="text-[#38485C] text-[16px] font-semibold">{quantity}</p>
                            </div>

                            {row.products && row.products.length > 0 && (
                              <div className="flex-1 max-w-[200px]">
                                <p className="text-[#707070] text-[12px] font-semibold">{t(`rows.purchasePrice`)}</p>
                                <p className="text-[#38485C] text-[16px] font-semibold">{formatAmount(product.price)}</p>
                              </div>
                            )}

                            {row.productQuantities?.length > 0 && (
                              <div className="flex-1 max-w-[200px]">
                                <p className="text-[#707070] text-[12px] font-semibold">{t(`columns.family`)}</p>
                                <p className="text-[#38485C] text-[16px] font-semibold break-words">
                                  {product.familyName || "N/A"}
                                </p>
                              </div>
                            )}

                            {row.products?.length > 0 && (
                              <div className="flex justify-end items-start max-w-[50px] mr-10">
                                <button
                                  className="w-8 h-8 flex items-center justify-center bg-white rounded-[12px] shadow-[0px_6px_26px_#626B7D12]"
                                  onClick={() => onDelete && onDelete(row, product)}
                                  aria-label={t("rows.delete")}
                                >
                                  <img src={deleteIcon} alt="" className="w-4 h-4" />
                                </button>
                              </div>
                            )}
                          </div>

                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Rows;